// ADMIN-FAHRT-ZUWEISUNG-001 — Dispatch-Platz: alle offenen Sofortfahrten auf einen Blick,
// eine Fahrt wählen, Fahrer zuweisen oder an einen anderen Fahrer übergeben.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeftRight, Car, Circle, Clock3, MapPin, RefreshCw, Users } from 'lucide-react';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import RideReassign, { RIDE_STATUS_LABEL } from '../components/RideReassign';
import DispatchReview from '../components/DispatchReview';
import { Empty, ErrorBox, Skeleton } from '../ui/kit';
import './Dispatch.css';

const STATUS_COLOR = { searching: '#4b5563', accepted: '#586b87', arrived: '#7c5a91', in_progress: '#9c27b0' };
const TYPE_LABEL = { standard: 'Basis', comfort: 'Komfort', premium: 'Business', xl: 'Family', max: 'Van', taxi: 'Taxi' };
const REFRESH_MS = 15000;

const money = (value) => Number(value || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const minutesSince = (value) => {
  if (!value) return null;
  const ms = Date.now() - new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`).getTime();
  return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 60000)) : null;
};

function RideCard({ ride, selected, onSelect }) {
  const waiting = minutesSince(ride.dispatch_wait_anchor || ride.created_at);
  const who = ride.driver_name
    ? <>Fahrer <strong>{ride.driver_name}</strong>{ride.company_name ? ` · ${ride.company_name}` : ''}</>
    : (ride.offered_driver_name ? <>Angebot bei <strong>{ride.offered_driver_name}</strong></> : <em>Noch kein Fahrer</em>);
  return (
    <li>
      <button
        type="button"
        className={`dispatch-ride${selected ? ' is-selected' : ''}${ride.assignable ? '' : ' is-locked'}`}
        style={{ '--dispatch-status': STATUS_COLOR[ride.status] || '#4b5563' }}
        onClick={() => ride.assignable && onSelect(ride.id)}
        aria-pressed={selected}
        aria-disabled={!ride.assignable}
        title={ride.assignable ? 'Fahrt wählen' : 'Laufende Fahrt — nicht zuweisbar'}
      >
        <header>
          <span className="dispatch-status"><i /> {ride.attention_reasons?.includes('gps_review') ? 'Prüfung · angehalten' : RIDE_STATUS_LABEL[ride.status] || ride.status}</span>
          <span className="dispatch-type">{TYPE_LABEL[ride.ride_type] || ride.ride_type || '—'}</span>
          <span className="dispatch-wait"><Clock3 size={11} /> {waiting == null ? '—' : `${waiting} Min.`}</span>
        </header>
        {ride.dispatch_attention && (
          <p className="dispatch-attention">
            {ride.attention_reasons?.includes('gps_review') ? 'GPS-Rückgabeprüfung · keine Vermittlung' : `Prüfung nötig · ${ride.genuine_return_count || 0} Rückgaben · ${waiting == null ? '—' : `${waiting} Min. gesamt`}`}
          </p>
        )}
        <p className="dispatch-customer"><strong>{ride.customer_name}</strong> · {money(ride.price)} € · {ride.payment_method === 'cash' ? 'Bar' : 'Karte'}</p>
        <p className="dispatch-route"><MapPin size={11} /> {ride.pickup_address}</p>
        <p className="dispatch-route"><Circle size={11} /> {ride.dropoff_address}</p>
        <p className="dispatch-driver">{who}</p>
      </button>
    </li>
  );
}

export default function Dispatch() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const loadRef = useRef(null);

  const load = useCallback(() => {
    setError(false);
    return api.get('/admin/dispatch/board')
      .then((response) => { setBoard(response.data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);
  loadRef.current = load;

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = setInterval(() => { if (loadRef.current) loadRef.current(); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const attentionOnly = new URLSearchParams(window.location.search).get('attention') === '1';
  const allRides = board?.rides || [];
  const rides = attentionOnly ? allRides.filter((ride) => ride.dispatch_attention) : allRides;
  const selected = rides.find((ride) => ride.id === selectedId) || null;
  useEffect(() => {
    if (selectedId && board && !rides.some((ride) => ride.id === selectedId && ride.assignable)) setSelectedId(null);
  }, [board, rides, selectedId]);
  useEffect(() => {
    if (!attentionOnly || selectedId || !board) return;
    const first = rides.find((ride) => ride.assignable);
    if (first) setSelectedId(first.id);
  }, [attentionOnly, board, rides, selectedId]);

  const open = rides.filter((ride) => ride.assignable);
  const counts = { searching: 0, accepted: 0, arrived: 0, in_progress: 0, admin_review: 0 };
  rides.forEach((ride) => { const status=ride.attention_reasons?.includes('gps_review') ? 'admin_review' : ride.status; counts[status] = (counts[status] || 0) + 1; });

  return (
    <div className="dispatch-page">
      <PageHeader
        title="Zuweisung"
        description="Offene Sofortfahrten einem Fahrer zuweisen oder an einen anderen Fahrer übergeben. Reservierungen laufen über das Partner-Portal."
        actions={
          <div className="dispatch-heading-actions">
            <span><Car size={13} /> {open.length} offen</span>
            <span><Users size={13} /> {board?.drivers_online ?? '—'} online · {board?.drivers_available ?? '—'} frei</span>
            <button type="button" onClick={load} disabled={loading}><RefreshCw size={14} /> {loading ? 'Lädt…' : 'Aktualisieren'}</button>
          </div>
        }
      />

      <section className="dispatch-strip" aria-label="Fahrtlage">
        {Object.entries(counts).map(([status, count]) => (
            <span key={status} style={{ '--dispatch-status': STATUS_COLOR[status] }}><i /> {status === 'admin_review' ? 'Angehalten' : RIDE_STATUS_LABEL[status]} <b>{count}</b></span>
        ))}
      </section>

      {error && <ErrorBox text="Dispatch-Lage konnte nicht geladen werden." onRetry={load} style={{ marginBottom: 12 }} />}

      <div className="dispatch-layout">
        <section className="dispatch-list-card">
          <header>
            <div><span className="dispatch-overline">Sofortfahrten</span><h2>{attentionOnly ? 'Fahrten zur Prüfung' : 'Offene Fahrten'}</h2></div>
            <span>Aktualisiert alle {REFRESH_MS / 1000} s</span>
          </header>
          {loading && !board ? (
            <div className="dispatch-loading"><Skeleton h={160} r={10} /></div>
          ) : rides.length === 0 ? (
            <Empty icon={Car} title={attentionOnly ? 'Keine Fahrt braucht Prüfung' : 'Keine offenen Sofortfahrten'} hint={attentionOnly ? 'Aktuell liegt keine Fahrt über dem Rückgabe- oder Wartezeit-Schwellwert.' : 'Sobald ein Kunde bucht, erscheint die Fahrt hier.'} />
          ) : (
            <ul className="dispatch-rides">
              {rides.map((ride) => (
                <RideCard key={ride.id} ride={ride} selected={ride.id === selectedId} onSelect={setSelectedId} />
              ))}
            </ul>
          )}
        </section>

        <aside className="dispatch-side">
          {selected ? (
            <>
              <div className="dispatch-selected">
                <span><ArrowLeftRight size={15} /></span>
                <div>
                  <small>Gewählte Fahrt</small>
                  <strong>{selected.customer_name} · {selected.pickup_address}</strong>
                </div>
              </div>
              <RideReassign
                key={selected.id}
                rideId={selected.id}
                compact
                onReassigned={() => { setSelectedId(null); load(); }}
              />
              <DispatchReview key={`review-${selected.id}`} rideId={selected.id} reviews={selected.dispatch_reviews} onChanged={load} />
            </>
          ) : (
            <div className="dispatch-placeholder">
              <ArrowLeftRight size={22} />
              <strong>Fahrt wählen</strong>
              <p>Links eine Fahrt im Status „Suche", „Angenommen" oder „Angekommen" antippen — dann erscheinen hier die Fahrer-Kandidaten.</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
